import React, { useRef, useState } from 'react';

import { isAxiosError } from 'axios';
import { CheckCircle2 } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { saveLoadDocuments } from 'lib/api/saveLoadDocuments';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { ThreeGDocumentTypeSelect } from './ThreeGDocumentTypeSelect';
import { ThreeGDocumentMessageType } from './constants';

type DocumentsSectionFormProps = {
  loadId?: number;
};

// Direct uploads send contentBase64 in the request body, which is capped by Fiber's
// default ~4 MiB BodyLimit (base64 expands ~4/3). Email attachments use S3 and support larger files.
const MAX_DIRECT_UPLOAD_BYTES = 2.5 * 1024 * 1024;

export const DocumentsSectionForm: React.FC<DocumentsSectionFormProps> = ({
  loadId,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<Maybe<File>>(null);
  const [messageType, setMessageType] = useState<ThreeGDocumentMessageType>(
    ThreeGDocumentMessageType.POD
  );
  const [isUploading, setIsUploading] = useState(false);
  const [lastUploadedDoc, setLastUploadedDoc] = useState<Maybe<string>>(null);

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_DIRECT_UPLOAD_BYTES) {
      toast({
        description:
          'File size exceeds the 2.5 MB direct-upload limit. Use Save attachments to 3G for larger email files.',
        variant: 'destructive',
      });
      setSelectedFile(null);
      clearFileInput();
      return;
    }
    setSelectedFile(file);
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Failed to read file.'));
          return;
        }
        const base64String = result.split(',')[1];
        if (!base64String) {
          reject(new Error('Failed to encode file.'));
          return;
        }
        resolve(base64String);
      };
      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsDataURL(file);
    });

  const handleDirectUpload = async () => {
    if (!loadId) {
      toast({
        description:
          'Identify or build the load in 3G before attaching documents.',
        variant: 'destructive',
      });
      return;
    }
    if (!selectedFile) return;

    setIsUploading(true);
    setLastUploadedDoc(null);

    try {
      const contentBase64 = await readFileAsBase64(selectedFile);
      const result = await saveLoadDocuments(loadId, {
        messageType,
        attachments: [
          {
            fileName: selectedFile.name,
            mimeType: selectedFile.type,
            contentBase64,
          },
        ],
      });

      if (result.isErr()) {
        toast({
          description: result.error.message,
          variant: 'destructive',
        });
        return;
      }

      const uploadResult = result.value.results[0];
      if (uploadResult?.success) {
        setLastUploadedDoc(
          `Doc #${uploadResult.docNum}${
            uploadResult.wasRenamed ? ' (sequenced)' : ''
          }`
        );
        setSelectedFile(null);
        clearFileInput();
        toast({
          description: 'Document attached to 3G load successfully!',
        });
      } else {
        toast({
          description:
            uploadResult?.error || 'Failed to attach document to 3G.',
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      if (
        (isAxiosError(err) && err.response?.status === 401) ||
        (err instanceof Error &&
          err.message === 'Extension context invalidated.')
      ) {
        throw err;
      }
      toast({
        description: 'Error uploading file.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Flex
      direction='col'
      gap='md'
      className='w-full p-3 bg-neutral-50 rounded-md border border-neutral-200'
    >
      <Flex direction='col' gap='sm'>
        <Typography variant='body-sm' className='font-medium text-neutral-800'>
          Upload Document to 3G Load
        </Typography>

        <ThreeGDocumentTypeSelect
          value={messageType}
          onChange={setMessageType}
          disabled={isUploading}
        />

        <Flex direction='col' gap='xs'>
          <Typography variant='body-xs' className='text-neutral-600'>
            File Attachment (PDF, JPG, PNG, TIFF up to 2.5 MB)
          </Typography>
          <input
            ref={fileInputRef}
            type='file'
            accept='.pdf,.jpg,.jpeg,.png,.tif,.tiff'
            onChange={handleFileChange}
            className='block w-full text-xs text-neutral-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-neutral-200 file:text-neutral-800 hover:file:bg-neutral-300'
          />
        </Flex>

        {lastUploadedDoc && (
          <Flex
            align='center'
            gap='xs'
            className='text-success-700 bg-success-50 p-2 rounded'
          >
            <CheckCircle2 className='h-4 w-4 shrink-0' />
            <Typography variant='body-xs'>
              Uploaded: {lastUploadedDoc}
            </Typography>
          </Flex>
        )}

        <Button
          type='button'
          size='sm'
          variant='secondary'
          disabled={!selectedFile || isUploading || !loadId}
          buttonNamePosthog={ButtonNamePosthog.UploadDocumentToThreeG}
          onClick={handleDirectUpload}
        >
          {isUploading ? <ButtonLoader /> : 'Upload to 3G'}
        </Button>
      </Flex>
    </Flex>
  );
};
